import { hasConsent } from '@/utils/cookieConsent';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

/**
 * Initialize Google Analytics with consent
 */
export const initializeAnalytics = (trackingId: string): void => {
  if (typeof window === 'undefined' || !trackingId) return;

  // Initialize dataLayer if it doesn't exist
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function(...args: any[]) {
    window.dataLayer?.push(args);
  };

  // Set default consent state
  window.gtag('consent', 'default', {
    analytics_storage: hasConsent('analytics') ? 'granted' : 'denied',
    ad_storage: hasConsent('marketing') ? 'granted' : 'denied',
    wait_for_update: 500
  });

  // Initialize GA
  window.gtag('js', new Date());
  window.gtag('config', trackingId, {
    anonymize_ip: true,
    send_page_view: hasConsent('analytics')
  });
};

/**
 * Update Google Analytics consent
 */
export const updateAnalyticsConsent = (analyticsConsent: boolean, marketingConsent: boolean): void => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;

  window.gtag('consent', 'update', {
    analytics_storage: analyticsConsent ? 'granted' : 'denied',
    ad_storage: marketingConsent ? 'granted' : 'denied'
  });
};

/**
 * Track page view (only if analytics consent is given)
 */
export const trackPageView = (url: string, title?: string): void => {
  if (!hasConsent('analytics') || typeof window.gtag !== 'function') return;

  window.gtag('event', 'page_view', {
    page_location: url,
    page_title: title
  });
};

/**
 * Track custom event (only if analytics consent is given)
 */
export const trackEvent = (
  eventName: string, 
  parameters: Record<string, any> = {}
): void => {
  if (!hasConsent('analytics') || typeof window.gtag !== 'function') return;

  window.gtag('event', eventName, parameters);
};

/**
 * Load Google Analytics script
 */
export const loadGoogleAnalytics = (trackingId: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('Window is not defined'));
      return;
    }

    // Check if script is already loaded
    if (document.querySelector(`script[src*="gtag/js?id=${trackingId}"]`)) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
    
    script.onload = () => {
      initializeAnalytics(trackingId);
      resolve();
    };
    
    script.onerror = () => {
      reject(new Error('Failed to load Google Analytics'));
    };

    document.head.appendChild(script);
  });
};
